import { FieldWrapper, getFieldErrorMessage } from "@birdwatch/ui/form";
import type { ComponentProps, ReactNode } from "react";
import { Textarea } from "@/components/ui/textarea";
import {
  type FieldBindingProps,
  PolyField,
  splitBinding,
} from "@/fields/_internal/poly-field";

type TextareaFieldBaseProps = Omit<
  ComponentProps<typeof Textarea>,
  "value" | "onChange" | "onBlur" | "className"
> & {
  label?: ReactNode;
  description?: ReactNode;
  optional?: boolean;
  /** Applied to the FieldWrapper root. */
  className?: string;
  /** Applied to the inner `<textarea>`. */
  inputClassName?: string;
  /** Called after the field's own handleBlur. */
  onBlur?: () => void;
  /** Fired AFTER the value is committed — for side effects only. */
  onValueChange?: (value: string) => void;
};

export type TextareaFieldProps = TextareaFieldBaseProps & FieldBindingProps;

export function TextareaField(props: TextareaFieldProps) {
  const [binding, rest] = splitBinding(props);
  const {
    label,
    description,
    optional,
    className,
    inputClassName,
    onBlur,
    onValueChange,
    ...textareaProps
  } = rest;
  return (
    <PolyField<string> {...binding}>
      {(field) => (
        <FieldWrapper
          label={label}
          description={description}
          optional={optional}
          error={getFieldErrorMessage(field)}
          className={className}
          disabled={textareaProps.disabled}
        >
          <Textarea
            {...textareaProps}
            className={inputClassName}
            value={field.state.value ?? ""}
            onChange={(e) => {
              const next = e.target.value;
              field.handleChange(next);
              onValueChange?.(next);
            }}
            onBlur={() => {
              field.handleBlur();
              onBlur?.();
            }}
          />
        </FieldWrapper>
      )}
    </PolyField>
  );
}
